import BaseController from "./BaseController";
import NavContainer from "sap/m/NavContainer";
import Theming from "sap/ui/core/Theming";
import ToolPage from "sap/tnt/ToolPage";
import Button from "sap/m/Button";
import Image from "sap/m/Image";
import View from "sap/ui/core/mvc/View";
import { CustomEvent } from "webapp/src/types/CustomEvent";
import Device from "sap/ui/Device";
import SentryService from "../src/services/SentryService";

/**
 * @namespace de.realcore.realseats.controller
 */
export default class Landing extends BaseController {
    private readonly logger = SentryService.createLogger("LandingController");

    public onInit(): void {
        SentryService.startSpan("controller.init", "Landing Controller Init", (span) => {
            try {
                span?.setAttribute("controller", "Landing");

                this.logger.info("Landing controller initialization started");

                // Restore theme preference from localStorage on init
                const theme = localStorage.getItem("theme") || "";
                this.logger.debug("Theme preference loaded", { theme });

                if (theme === "sap_horizon_dark") {
                    this.onButtonToggleDarkModePress();
                }

                // Set controller context in Sentry
                SentryService.setContext("current_controller", {
                    name: "Landing",
                    initialized: true,
                    timestamp: new Date().toISOString()
                });

                SentryService.addBreadcrumb("Landing controller initialized", "lifecycle", "info", {
                    theme
                });

                this.logger.info("Landing controller initialization completed");
            } catch (error) {
                span?.setAttribute("error", true);
                this.handleError(error as Error, "Failed to initialize Landing controller", {
                    operation: "init"
                });
                throw error;
            }
        });
    }

    public onButtonMenuPress(): void {
        SentryService.instrumentUIInteraction(
            "button",
            "menu_toggle",
            () => {
                const toolPage = this.byId("idToolPage") as ToolPage;
                const currentExpanded = toolPage.getSideExpanded();
                const newExpanded = !currentExpanded;

                toolPage.setSideExpanded(newExpanded);

                this.logger.debug("Menu toggled", {
                    previousState: currentExpanded,
                    newState: newExpanded
                });

                SentryService.addBreadcrumb(
                    `Menu ${newExpanded ? "opened" : "closed"}`,
                    "ui",
                    "info",
                    { expanded: newExpanded }
                );
            },
            "menu-toggle-button",
            {
                isMobile: Device.system.phone,
                isTablet: Device.system.tablet
            }
        );
    }

    public onButtonToggleDarkModePress(): void {
        const lightLogoSrc = "realseats/image/RealCore_Group_light.png";
        const darkLogoSrc = "realseats/image/RealCore_Group_dark.png";

        const currentTheme: string = Theming.getTheme();
        const isDarkMode: boolean = currentTheme === "sap_horizon_dark";

        const newTheme: string = isDarkMode ? "sap_horizon" : "sap_horizon_dark";
        Theming.setTheme(newTheme);
        localStorage.setItem("theme", newTheme); // Save theme preference

        const imageControl = this.byId("idLogoImage") as Image;
        imageControl.setSrc(isDarkMode ? lightLogoSrc : darkLogoSrc);

        const buttonControl = this.byId("darkModeToggleButton") as Button;
        buttonControl.setIcon(isDarkMode ? "sap-icon://dark-mode" : "sap-icon://light-mode");
    }

    public async onSideNavigationItemSelect(oEvent: CustomEvent): Promise<void> {
        const oSelectedItem = oEvent.getParameter("item");
        const sKey = oEvent.getParameter("item").getKey();

        return SentryService.instrumentUIInteraction(
            "navigation",
            "side_menu_select",
            async () => {
                return this.executeWithErrorHandling(
                    `Navigate to ${sKey}`,
                    async () => {
                        const toolPage = this.byId("idToolPage") as ToolPage;
                        const pageContainer = this.byId("idNavContainer") as NavContainer;
                        const oView: View = this.getView();
                        const sPageId = oView.createId(sKey + "Page");
                        const sViewName = `de.realcore.realseats.view.page.${sKey}`;

                        this.logger.info("Navigation item selected", {
                            key: sKey,
                            pageId: sPageId,
                            viewName: sViewName
                        });

                        if (sKey === "reportBug") {
                            SentryService.addBreadcrumb(
                                "User selected bug report option",
                                "navigation",
                                "info",
                                { destination: "bug_report" }
                            );
                            return this.onBug();
                        }

                        // Check if the selected item has subpages
                        const aSubItems = oSelectedItem.getItems();
                        if (aSubItems && aSubItems.length > 0) {
                            // Toggle expanded state if there are subpages
                            const bExpanded = oSelectedItem.getExpanded();
                            oSelectedItem.setExpanded(!bExpanded);

                            this.logger.debug("Menu item expanded/collapsed", {
                                key: sKey,
                                expanded: !bExpanded,
                                subItemsCount: aSubItems.length
                            });

                            return; // Exit, do not navigate to a page
                        }

                        // Check if the page already exists in the container
                        let oPage = this.byId(sPageId) as View;
                        let pageLoadMethod = "existing";

                        if (!oPage) {
                            try {
                                // Try to load the target page view
                                this.logger.debug("Loading target page view", {
                                    viewName: sViewName
                                });
                                oPage = await this.loadPageView(sPageId, sViewName);
                                pageLoadMethod = "target";
                            } catch (error) {
                                // Load the fallback page if the target page is not found
                                this.logger.warn("Target page not found, loading fallback", {
                                    targetView: sViewName,
                                    error: (error as Error).message
                                });

                                const sFallbackPageId = oView.createId("EmployeeSearchPage");
                                oPage = this.byId(sFallbackPageId) as View;
                                pageLoadMethod = "fallback_existing";

                                // Fallback page already created?
                                if (!oPage) {
                                    oPage = await this.loadPageView(
                                        sFallbackPageId,
                                        "de.realcore.realseats.view.page.EmployeeSearch"
                                    );
                                    pageLoadMethod = "fallback_new";
                                }
                            }
                            // Add the page to the NavContainer
                            pageContainer.addPage(oPage);
                        }

                        // Navigate to the page
                        pageContainer.to(oPage.getId());

                        SentryService.addBreadcrumb(
                            "Page navigation completed",
                            "navigation",
                            "info",
                            {
                                targetKey: sKey,
                                pageId: oPage.getId(),
                                loadMethod: pageLoadMethod
                            }
                        );

                        // Check whether it is a mobile device and whether SideNavigation is open
                        if (Device.system.phone && toolPage.getSideExpanded()) {
                            toolPage.setSideExpanded(false); // close SideNavigation
                            this.logger.debug("Closed side navigation on mobile");
                        }

                        this.logger.info("Navigation completed successfully", {
                            destination: sKey,
                            loadMethod: pageLoadMethod,
                            isMobile: Device.system.phone
                        });
                    },
                    `Failed to navigate to ${sKey}`
                );
            },
            "side-navigation-item",
            {
                targetKey: sKey,
                isMobile: Device.system.phone,
                hasSubItems: !!oSelectedItem.getItems()?.length
            }
        );
    }

    private async loadPageView(sPageId: string, sViewName: string): Promise<View> {
        return SentryService.startSpan("view.load", `Load View: ${sViewName}`, async (span) => {
            try {
                const startTime = Date.now();

                span?.setAttribute("view.id", sPageId);
                span?.setAttribute("view.name", sViewName);
                span?.setAttribute("view.type", "XML");

                this.logger.debug("Starting view creation", {
                    pageId: sPageId,
                    viewName: sViewName
                });

                const oView = await View.create({
                    id: sPageId,
                    viewName: sViewName,
                    type: "XML"
                });

                const loadTime = Date.now() - startTime;

                span?.setAttribute("view.load_time", loadTime);

                SentryService.addBreadcrumb("View loaded successfully", "performance", "info", {
                    viewName: sViewName,
                    pageId: sPageId,
                    loadTime: `${loadTime}ms`
                });

                this.logger.info("View created successfully", {
                    pageId: sPageId,
                    viewName: sViewName,
                    loadTime: `${loadTime}ms`
                });

                return oView;
            } catch (error) {
                span?.setAttribute("error", true);

                this.logger.error("Failed to create view", {
                    pageId: sPageId,
                    viewName: sViewName,
                    error: (error as Error).message
                });

                // Don't capture this as an error since it's expected for some views
                // The caller will handle it as a fallback scenario
                throw error;
            }
        });
    }
}
